import { ReactElement, useMemo, useState, useEffect } from 'react';
import Button from '../../../../components/Button';
import { HiArrowLongUp, HiArrowLongDown, HiArrowUpTray, HiArrowDownTray } from 'react-icons/hi2';
import { numberToLetters } from '../../../../utils/letterNumber';
import { Config } from 'types/Config';

interface SessionRunOrderProps {
  configs: Config[];
  plannedRunConfigIds: string[];
  setPlannedRunConfigIds: (ids: string[]) => void;
  isEdit: boolean;
}

interface PlannedRunEntry {
  id: number;
  configId: string;
}

export default function SessionRunOrder({
  configs,
  plannedRunConfigIds,
  setPlannedRunConfigIds,
  isEdit,
}: SessionRunOrderProps): ReactElement {
  const configsById = (configs || []).reduce((map: any, config: any) => {
    map[config.id] = config;
    return map;
  }, new Map());

  const [lastMovedId, setLastMovedId] = useState<number | null>(null);
  const [plannedRunEntries, setPlannedRunEntries] = useState<PlannedRunEntry[]>(() =>
    plannedRunConfigIds
      .filter((configId) => configsById[configId])
      .map((configId, index) => ({
        id: index,
        configId,
      })),
  );
  const [positions, setPositions] = useState<Record<number, number>>({});

  // Get all actual runs from configs
  const actualRuns = useMemo(() => {
    const runs: Array<{ run: any; config: any }> = [];
    configs.forEach((config) => {
      if (config.runs) {
        config.runs.forEach((run) => {
          if (run.id) {
            // Only include runs that have an ID (actual runs)
            runs.push({ run, config });
          }
        });
      }
    });
    return runs.sort((a, b) => (a.run.run_number || 0) - (b.run.run_number || 0));
  }, [configs]);

  // Update positions when entries change
  useEffect(() => {
    const newPositions: Record<number, number> = {};
    plannedRunEntries.forEach((entry, index) => {
      newPositions[entry.id] = index;
    });
    setPositions(newPositions);
  }, [plannedRunEntries]);

  const moveItem = (fromIndex: number, toIndex: number) => {
    const newEntries = [...plannedRunEntries];
    const [movedEntry] = newEntries.splice(fromIndex, 1);
    newEntries.splice(toIndex, 0, movedEntry);

    setLastMovedId(movedEntry.id);
    setPlannedRunEntries(newEntries);
    setPlannedRunConfigIds(newEntries.map((entry) => entry.configId));
  };

  const runRowStyle = 'cr-border-solid cr-border-cirrusBlue-100 cr-border-b-2 cr-p-2 cr-pl-4 cr-min-w-0';
  const buttonStyle =
    'cr-bg-cirrusBlue-50 hover:cr-bg-cirrusBlue-100 cr-p-0 cr-pl-0 cr-gap-0 cr-w-10 cr-h-8 cr-flex cr-items-center cr-justify-center';
  const upArrowStyle = 'cr-text-green-600 cr-ml-1';
  const downArrowStyle = 'cr-text-[#e53e3e] cr-ml-1';

  return (
    <>
      <style>
        {`
          @keyframes highlightYellow {
            0% { background-color: #fff8b2; }
            100% { background-color: #CCDDE5; }
          }
          .highlight-animation {
            animation: highlightYellow 1s ease-out forwards;
            z-index: 1;
          }
          .planned-run-row {
            transition: transform 0.3s ease-out;
            position: relative;
          }
        `}
      </style>
      <div className="cr-flex cr-flex-col cr-gap-4">
        <div className="cr-grid cr-grid-cols-[1fr_2fr_2fr_12fr_2fr] cr-shadow-lg">
          <div
            className="cr-col-span-5 cr-grid cr-grid-cols-subgrid cr-rounded-t-lg cr-border-2 cr-border-solid cr-border-cirrusBlue-100 cr-bg-cirrusBlue-50 cr-p-3 cr-text-[19px] cr-uppercase cr-text-cirrusBlue-400 md:cr-text-[16px]"
            style={{ fontFamily: '\'Poppins\', sans-serif', fontWeight: '500' }}
          >
            <div className="cr-min-w-0 cr-pl-4"></div>
            <div className="cr-min-w-0 cr-pl-4">Run #</div>
            <div className="cr-min-w-0 cr-pl-4">Config</div>
            <div className="cr-min-w-0 cr-pl-4">Description</div>
            <div className="cr-min-w-0 cr-truncate cr-pl-4">{isEdit && 'Actions'}</div>
          </div>
          <div className="cr-col-span-5 cr-grid cr-grid-cols-subgrid cr-rounded-b-lg cr-border-2 cr-border-t-0 cr-border-solid cr-border-cirrusBlue-100 cr-bg-cirrusBlue-25 cr-pb-6">
            {/* Actual Runs */}
            {actualRuns.map(({ run, config }, index) => {
              const isLastRun = index === actualRuns.length - 1;
              const actualRunRowStyle = `${runRowStyle} ${isLastRun && 'cr-border-b-4 cr-border-solid;'}`;
              return (
                <div
                  key={`actual-${run.id}`}
                  className={`cr-col-span-5 cr-grid cr-grid-cols-subgrid cr-py-1 
                              cr-font-main cr-text-[17px] cr-italic cr-text-gray-500 md:cr-text-[14px] 
                  ${isLastRun && 'cr-mb-3'}`}
                >
                  <div></div>
                  <div className={actualRunRowStyle}>{run.run_number}</div>
                  <div className={actualRunRowStyle}>
                    {config.letter_number !== undefined ? numberToLetters(config.letter_number) : ''}
                  </div>
                  <div className={actualRunRowStyle}>{config.description || '-'}</div>
                  <div className={actualRunRowStyle}></div>
                </div>
              );
            })}

            {/* Planned Runs */}
            {plannedRunEntries.map((entry, index) => {
              const config = configsById[entry.configId];

              const showArrow = entry.id !== index;
              const movement = entry.id < index ? 'down' : 'up';
              const arrowCount = Math.abs(entry.id - index);
              const arrows =
                arrowCount > 3
                  ? `${movement === 'up' ? '↑' : '↓'}x${arrowCount}`
                  : (movement === 'up' ? '↑' : '↓').repeat(arrowCount);

              const isLastMoved = entry.id === lastMovedId;
              const plannedRunNumber = actualRuns.length + index + 1;
              const originalPosition = positions[entry.id] ?? index;
              const translateY = (originalPosition - index) * 100;
              const plannedRunRowClasses = [
                'planned-run-row',
                'cr-col-span-5',
                'cr-grid',
                'cr-grid-cols-subgrid',
                'cr-font-main',
                'cr-text-[17px]',
                'md:cr-text-[14px]',
                isLastMoved ? 'highlight-animation cr-bg-cirrusBlue-35' : '',
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <div
                  key={`${entry.id}-${index}`}
                  className={plannedRunRowClasses}
                  style={{
                    transform: `translateY(${translateY}%)`,
                  }}
                >
                  <div></div>
                  <div className={`${runRowStyle} cr-flex cr-items-center`}>
                    {plannedRunNumber}
                    {showArrow && <span className={movement === 'up' ? upArrowStyle : downArrowStyle}>{arrows}</span>}
                  </div>
                  <div className={runRowStyle}>
                    {config.letter_number !== undefined ? numberToLetters(config.letter_number) : ''}
                  </div>
                  <div className={runRowStyle}>{config.description || '-'}</div>
                  <div className={`${runRowStyle} cr-grid cr-grid-cols-[1fr_1fr_1fr_1fr] cr-gap-1`}>
                    {isEdit && (
                      <>
                        <div className="cr-flex cr-justify-end">
                          {index > 0 && (
                            <Button className={buttonStyle} onClick={() => moveItem(index, 0)}>
                              <HiArrowUpTray />
                            </Button>
                          )}
                        </div>
                        <div className="cr-flex cr-justify-end">
                          {index > 0 && (
                            <Button className={buttonStyle} onClick={() => moveItem(index, index - 1)}>
                              <HiArrowLongUp />
                            </Button>
                          )}
                        </div>
                        <div className="cr-flex cr-justify-start">
                          {index < plannedRunEntries.length - 1 && (
                            <Button className={buttonStyle} onClick={() => moveItem(index, index + 1)}>
                              <HiArrowLongDown />
                            </Button>
                          )}
                        </div>
                        <div className="cr-flex cr-justify-start">
                          {index < plannedRunEntries.length - 1 && (
                            <Button
                              className={buttonStyle}
                              onClick={() => moveItem(index, plannedRunEntries.length - 1)}
                            >
                              <HiArrowDownTray />
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
